/**
 * Teléfonos, escritos sin errores.
 *
 * A fiado client's phone is how the shop reminds them and sends their statement, so one wrong
 * digit is a debt nobody can chase. A Nicaraguan number is eight digits — landlines start with
 * 2, mobiles with 5, 7 or 8 — and that is what the field lets through: digits only, never more
 * than eight, grouped the way people read them out ("8812 3456") while they are typed.
 *
 * Pasted with the country code in front (+505, 00505, or eleven digits starting 505), a number
 * shows it and is saved without it, the way everybody writes them. A number from another
 * country is the one way past the eight digits: it starts with "+" and says its own country.
 *
 * None of this is in the event schema. Old events carry whatever was typed back then, and a
 * rule added to the schema now would make them fail on every replay; the rule lives where the
 * number is typed.
 */

/** Landlines start with 2; mobiles with 5, 7 or 8. */
const PRIMEROS = new Set(['2', '5', '7', '8']);
export const DIGITOS_NICARAGUA = 8;
/** E.164: at most fifteen digits, the country code included. */
const MAX_EXTRANJERO = 15;
/** Fewer than this is not a phone number anywhere. */
const MIN_EXTRANJERO = 8;

const soloDigitos = (s: string) => s.replace(/\D/g, '');

/** A local number grouped the way it is said aloud: 8812 3456. */
const agrupar = (d: string) => (d.length > 4 ? `${d.slice(0, 4)} ${d.slice(4)}` : d);

/**
 * What the field holds after every keystroke or paste. Only digits get in, and at most eight of
 * a Nicaraguan number; the space goes where people pause.
 */
export function formatearTelefono(texto: string): string {
  const escrito = texto.trim();
  const d = soloDigitos(escrito);
  if (escrito.startsWith('+')) {
    if (d.startsWith('505')) {
      const local = d.slice(3, 3 + DIGITOS_NICARAGUA);
      return local ? `+505 ${agrupar(local)}` : '+505';
    }
    return `+${d.slice(0, MAX_EXTRANJERO)}`;
  }
  // 00 is how an international number is dialled from Nicaragua: the same as a +.
  if (d.startsWith('00')) return formatearTelefono(`+${d.slice(2)}`);
  // More than eight digits that start with the country code: the code was typed without its +.
  if (d.length > DIGITOS_NICARAGUA && d.startsWith('505')) return formatearTelefono(`+${d}`);
  return agrupar(d.slice(0, DIGITOS_NICARAGUA));
}

export type EstadoTelefono =
  | { tipo: 'vacio' }
  | { tipo: 'incompleto'; faltan: number; extranjero: boolean }
  | { tipo: 'invalido'; motivo: 'prefijo' | 'largo' }
  | { tipo: 'valido'; guardar: string };

/** Whether what is in the field is a number, and what to save if it is. */
export function revisarTelefono(texto: string): EstadoTelefono {
  const escrito = texto.trim();
  if (!escrito) return { tipo: 'vacio' };
  const d = soloDigitos(escrito);
  let local = d;
  if (escrito.startsWith('+') || d.startsWith('00')) {
    const internacional = escrito.startsWith('+') ? d : d.slice(2);
    if (!internacional.startsWith('505')) {
      if (internacional.length < MIN_EXTRANJERO) {
        return { tipo: 'incompleto', faltan: MIN_EXTRANJERO - internacional.length, extranjero: true };
      }
      if (internacional.length > MAX_EXTRANJERO) return { tipo: 'invalido', motivo: 'largo' };
      return { tipo: 'valido', guardar: `+${internacional}` };
    }
    local = internacional.slice(3);
  } else if (d.length > DIGITOS_NICARAGUA && d.startsWith('505')) {
    local = d.slice(3);
  }
  if (local.length < DIGITOS_NICARAGUA) {
    return { tipo: 'incompleto', faltan: DIGITOS_NICARAGUA - local.length, extranjero: false };
  }
  if (local.length > DIGITOS_NICARAGUA) return { tipo: 'invalido', motivo: 'largo' };
  if (!PRIMEROS.has(local[0]!)) return { tipo: 'invalido', motivo: 'prefijo' };
  return { tipo: 'valido', guardar: agrupar(local) };
}

/** Whether a form may be saved with this in the phone: a good number, or none where none is fine. */
export const telefonoAceptable = (texto: string, obligatorio: boolean): boolean => {
  const e = revisarTelefono(texto);
  return e.tipo === 'valido' || (e.tipo === 'vacio' && !obligatorio);
};

/**
 * Whether a search is for this phone: by its digits, however either was spaced. "88123456"
 * finds "8812 3456" — otherwise searching for somebody by their number comes up empty, and the
 * picker offers to add them a second time. Three digits at least, so a search for a name with
 * a number in it is not taken for a phone.
 */
export function coincideTelefono(telefono: string | null | undefined, busqueda: string): boolean {
  const buscado = soloDigitos(busqueda);
  return buscado.length >= 3 && soloDigitos(telefono ?? '').includes(buscado);
}

/** What goes in the event: the number the way the shop writes it, or nothing. */
export function telefonoParaGuardar(texto: string): string | null {
  const e = revisarTelefono(texto);
  return e.tipo === 'valido' ? e.guardar : null;
}
